import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  Users,
  Briefcase,
  Clock,
  CheckCircle,
  XCircle,
  PauseCircle,
  Eye,
  Ban,
  Check,
  Home,
  LogOut,
  Trash2,
  Plus,
  UserPlus,
  DollarSign,
  ShoppingCart,
  Menu,
  FileText,
  Settings,
  BarChart3,
  TrendingUp,
  Activity,
  Globe,
  UserCheck,
  LogIn,
  Megaphone,
  Download
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Checkbox } from "../../../components/ui/checkbox";
import { exportToExcel } from "../../../utils/exportToExcel";
import { useConvexAuth, useConvexProjects, useConvexInvestments, useConvexProjectSales } from "../../../services/convexService";
import { toast } from "sonner";
import { useAuth } from "../../../contexts/AuthContext";
import { Id } from "../../../../backend/_generated/dataModel";
import { useLanguage } from "../../../contexts/LanguageContext";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";
import BannerManagement from "../../../features/admin/components/BannerManagement";

const AdminDashboard = () => {
  const { getUsersByStatus, updateUserStatus, getUsersByRole, deleteUser, registerAdmin, registerInvestor, registerProjectHolder, getUser } = useConvexAuth();
  const { getProjectsByStatus, updateProjectStatus, getProjectsForInvestors, deleteProject, getProjectById } = useConvexProjects();
  const { getInvestmentsByProject, getInvestmentsByInvestor, getInvestmentsByProjectOwner, approveInvestmentByAdmin, rejectInvestment, refundInvestment, getInvestmentById, getInvestmentsByStatus } = useConvexInvestments();
  const { getAllProjectSales, getProjectSalesByStatus } = useConvexProjectSales();
  const [activeTab, setActiveTab] = useState("overview");
  const [userRoleTab, setUserRoleTab] = useState("all");
  const [projectStatusTab, setProjectStatusTab] = useState("all");
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportSelections, setExportSelections] = useState({
    users: true,
    projects: true,
    investments: true,
    sales: true,
  });

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();

  // Get data from Convex
  const pendingUsers = getUsersByStatus("pending_approval");
  const approvedUsers = getUsersByStatus("approved");
  const rejectedUsers = getUsersByStatus("rejected");
  const suspendedUsers = getUsersByStatus("suspended");

  // Get users by role
  const investors = getUsersByRole("investor");
  const projectHolders = getUsersByRole("project_holder");
  const admins = getUsersByRole("admin");

  const pendingProjects = getProjectsByStatus("pending_approval");
  const approvedProjects = getProjectsByStatus("approved");
  const draftProjects = getProjectsByStatus("draft");
  const fundedProjects = getProjectsByStatus("funded");
  const completedProjects = getProjectsByStatus("completed");

  // Get investment data for admin dashboard
  const pendingInvestments = getInvestmentsByStatus("pending");
  const projectHolderApprovedInvestments = getInvestmentsByStatus("project_holder_approved");
  const confirmedInvestments = getInvestmentsByStatus("confirmed");
  const rejectedInvestments = getInvestmentsByStatus("rejected");

  // Get project sales data for admin dashboard
  const allProjectSales = getAllProjectSales();
  const pendingProjectSales = getProjectSalesByStatus("pending");
  const acceptedProjectSales = getProjectSalesByStatus("accepted");
  const completedProjectSales = getProjectSalesByStatus("completed");
  const cancelledProjectSales = getProjectSalesByStatus("cancelled");

  // Combine all projects for the "All Projects" view
  const allProjects = [
    ...(pendingProjects || []),
    ...(approvedProjects || []),
    ...(draftProjects || []),
    ...(fundedProjects || []),
    ...(completedProjects || [])
  ];

  const allApprovedProjects = getProjectsForInvestors(); // This gets all approved projects

  const handleApproveUser = async (userId: Id<"users">) => {
    try {
      await updateUserStatus({ userId, status: "approved" as const });
      toast.success("User approved successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId: Id<"users">) => {
    try {
      await updateUserStatus({ userId, status: "rejected" as const });
      toast.success("User rejected successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to reject user");
    }
  };

  const handleSuspendUser = async (userId: Id<"users">) => {
    try {
      await updateUserStatus({ userId, status: "suspended" as const });
      toast.success("User suspended successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to suspend user");
    }
  };

  const handleDeleteUser = async (userId: Id<"users">) => {
    if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      try {
        await deleteUser({ userId });
        toast.success("User deleted successfully");
      } catch (error: any) {
        toast.error(error.message || "Failed to delete user");
      }
    }
  };

  const handleApproveProject = async (projectId: Id<"projects">) => {
    try {
      await updateProjectStatus({ projectId, status: "approved" as const });
      toast.success("Project approved successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to approve project");
    }
  };

  const handleRejectProject = async (projectId: Id<"projects">) => {
    try {
      await updateProjectStatus({ projectId, status: "draft" as const });
      toast.success("Project rejected successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to reject project");
    }
  };

  const handleDeleteProject = async (projectId: Id<"projects">) => {
    if (window.confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
      try {
        await deleteProject({ projectId });
        toast.success("Project deleted successfully");
      } catch (error: any) {
        toast.error(error.message || "Failed to delete project");
      }
    }
  };

  const handleApproveInvestment = async (investmentId: Id<"investments">) => {
    try {
      if (!user?.id) {
        toast.error("You must be logged in as an admin to approve investments");
        return;
      }
      await approveInvestmentByAdmin({ investmentId, approverId: user.id });
      toast.success("Investment approved successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to approve investment");
    }
  };

  const handleRejectInvestment = async (investmentId: Id<"investments">) => {
    try {
      if (!user?.id) {
        toast.error("You must be logged in as an admin to reject investments");
        return;
      }
      await rejectInvestment({ investmentId, rejectorId: user.id, reason: "Admin rejected" });
      toast.success("Investment rejected successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to reject investment");
    }
  };

  const handleRefundInvestment = async (investmentId: Id<"investments">) => {
    try {
      if (!user?.id) {
        toast.error("You must be logged in as an admin to refund investments");
        return;
      }
      await refundInvestment({ investmentId });
      toast.success("Investment refunded successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to refund investment");
    }
  };

  const handleExport = () => {
    const sheets = [];

    const getStatusLabel = (status: string) => {
      const statusMap: Record<string, string> = {
        "pending_approval": t("admin.statusPending"),
        "approved": t("admin.statusApproved"),
        "rejected": t("admin.statusRejected"),
        "suspended": t("admin.statusSuspended"),
        "draft": t("admin.statusDraft"),
        "funded": t("admin.statusFunded"),
        "completed": t("admin.statusCompleted"),
        "pending": t("admin.statusPending"),
        "accepted": t("admin.statusAccepted"),
        "cancelled": t("admin.statusCancelled")
      };
      return statusMap[status] || status;
    };

    if (exportSelections.users) {
      // Investors Sheet
      const investorsData = (investors || []).map(u => ({
        [t("common.name")]: u.name,
        [t("common.email")]: u.email,
        [t("common.status")]: getStatusLabel(u.status),
        [t("common.phone")]: (u as any).phone || "",
        [t("common.company")]: (u as any).company || "",
        [t("common.investmentCapacity")]: (u as any).investmentCapacity || "",
        [t("admin.sector")]: (u as any).sector || "",
        [t("common.created")]: new Date(u.createdAt).toLocaleString(language === 'ar' ? 'ar-DZ' : 'en-US')
      }));
      sheets.push({ sheetName: t("admin.investors"), data: investorsData });

      // Project Holders Sheet
      const projectHoldersData = (projectHolders || []).map(u => ({
        [t("common.name")]: u.name,
        [t("common.email")]: u.email,
        [t("common.status")]: getStatusLabel(u.status),
        [t("common.phone")]: (u as any).phone || "",
        [t("common.company")]: (u as any).company || "",
        [t("admin.sector")]: (u as any).sector || "",
        [t("common.created")]: new Date(u.createdAt).toLocaleString(language === 'ar' ? 'ar-DZ' : 'en-US')
      }));
      sheets.push({ sheetName: t("admin.projectHolders"), data: projectHoldersData });

      // Admins Sheet (optional but good for completeness)
      if (admins && admins.length > 0) {
        const adminsData = admins.map(u => ({
          [t("common.name")]: u.name,
          [t("common.email")]: u.email,
          [t("common.status")]: getStatusLabel(u.status),
          [t("common.created")]: new Date(u.createdAt).toLocaleString(language === 'ar' ? 'ar-DZ' : 'en-US')
        }));
        sheets.push({ sheetName: t("admin.admins") || "Admins", data: adminsData });
      }
    }

    if (exportSelections.projects) {
      const projectsData = allProjects.map(p => ({
        [t("admin.title")]: p.title,
        [t("admin.description")]: p.description,
        [t("common.status")]: getStatusLabel(p.status),
        [t("common.goal")]: p.fundingGoal,
        [t("common.raised")]: (p as any).currentFunding || 0,
        [t("common.category")]: p.category,
        [t("common.created")]: new Date(p.createdAt).toLocaleString(language === 'ar' ? 'ar-DZ' : 'en-US')
      }));
      sheets.push({ sheetName: t("admin.exportProjects"), data: projectsData });
    }

    if (exportSelections.investments) {
      const investmentsData = [
        ...(pendingInvestments || []),
        ...(projectHolderApprovedInvestments || []),
        ...(confirmedInvestments || []),
        ...(rejectedInvestments || [])
      ].map(i => {
        const invUser = getUserById(i.investorId);
        const project = getProjectByIdHelper(i.projectId);
        return {
          [t("admin.investor")]: invUser?.name || i.investorId,
          [t("common.project")]: project?.title || i.projectId,
          [t("admin.amount")]: i.amount,
          [t("common.status")]: getStatusLabel(i.status),
          [t("common.created")]: new Date(i.createdAt).toLocaleString(language === 'ar' ? 'ar-DZ' : 'en-US')
        };
      });
      sheets.push({ sheetName: t("admin.exportInvestments"), data: investmentsData });
    }

    if (exportSelections.sales) {
      const salesData = (allProjectSales || []).map(s => {
        const project = getProjectByIdHelper(s.projectId);
        return {
          [t("common.project")]: project?.title || s.projectId,
          [t("admin.amount")]: s.saleAmount,
          [t("common.status")]: getStatusLabel(s.status),
          [t("admin.agreement")]: s.saleAgreement || "",
          [t("common.created")]: new Date(s.createdAt).toLocaleString(language === 'ar' ? 'ar-DZ' : 'en-US')
        };
      });
      sheets.push({ sheetName: t("admin.exportSales"), data: salesData });
    }

    if (sheets.length === 0) {
      toast.error(t("admin.exportNoSelection"));
      return;
    }

    const filename = language === 'ar' ? `تصدير_بيانات_المدير_${new Date().toISOString().split('T')[0]}` : `KnowVest_Admin_Export_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(sheets, filename);
    setIsExportDialogOpen(false);
    toast.success(t("admin.exportSuccess") || "Excel file generated successfully");
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  // Helper functions to get user and project details
  const getUserById = (userId: Id<"users">) => {
    const allUsers = [
      ...(pendingUsers || []),
      ...(approvedUsers || []),
      ...(rejectedUsers || []),
      ...(suspendedUsers || []),
      ...(investors || []),
      ...(projectHolders || []),
      ...(admins || [])
    ];
    return allUsers.find(user => user._id === userId);
  };

  const getProjectByIdHelper = (projectId: Id<"projects">) => {
    const allProjects = [
      ...(pendingProjects || []),
      ...(approvedProjects || []),
      ...(draftProjects || []),
      ...(fundedProjects || []),
      ...(completedProjects || [])
    ];
    return allProjects.find(project => project._id === projectId);
  };

  return (
    <div className="min-h-screen flex flex-col" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => document.getElementById('admin-mobile-sidebar')?.classList.toggle('hidden')}
        >
          <Menu className="w-4 h-4" />
        </Button>
      </div>

      {/* Mobile Sidebar */}
      <aside
        id="admin-mobile-sidebar"
        className="lg:hidden fixed inset-0 z-40 bg-black/50 hidden"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.target as HTMLElement).classList.add('hidden');
          }
        }}
      >
        <div className="w-64 h-full bg-card p-4 ml-auto">
          {/* Logo and Text removed from sidebar header */}
          <div className="h-8"></div>

          <nav className="space-y-2 mb-8">
            <Link to="/admin" onClick={() => document.getElementById('admin-mobile-sidebar')?.classList.add('hidden')}>
              <Button variant={location.pathname === "/admin" ? "default" : "ghost"} className="w-full justify-start gap-3">
                <Users className="w-4 h-4" />
                {t("dashboard.admin.title")}
              </Button>
            </Link>
            <Link to="/admin/create-account" onClick={() => document.getElementById('admin-mobile-sidebar')?.classList.add('hidden')}>
              <Button variant={location.pathname === "/admin/create-account" ? "default" : "ghost"} className="w-full justify-start gap-3">
                <UserPlus className="w-4 h-4" />
                {t("dashboard.admin.createAccount")}
              </Button>
            </Link>
            <Link to="/admin/create-project-sale" onClick={() => document.getElementById('admin-mobile-sidebar')?.classList.add('hidden')}>
              <Button variant={location.pathname === "/admin/create-project-sale" ? "default" : "ghost"} className="w-full justify-start gap-3">
                <ShoppingCart className="w-4 h-4" />
                {t("projectSale.createSaleTitle") || "Create Project Sale"}
              </Button>
            </Link>
            <Link to="/admin/settings" onClick={() => document.getElementById('admin-mobile-sidebar')?.classList.add('hidden')}>
              <Button variant={location.pathname === "/admin/settings" ? "default" : "ghost"} className="w-full justify-start gap-3">
                <Settings className="w-4 h-4" />
                {t("common.settings")}
              </Button>
            </Link>
          </nav>

          <div className="space-y-2">
            <Link to="/home" onClick={() => document.getElementById('admin-mobile-sidebar')?.classList.add('hidden')}>
              <Button variant="outline" className="w-full justify-start gap-3">
                <Home className="w-4 h-4" />
                {t("navigation.home")}
              </Button>
            </Link>
            <Button
              variant="destructive"
              className="w-full justify-start gap-3"
              onClick={() => {
                logout();
                navigate("/home");
                document.getElementById('admin-mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <LogOut className="w-4 h-4" />
              {t("common.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8 w-full">
          {/* Text content */}
          <div>
            <h1 className="text-3xl font-bold">{t("dashboard.admin.title")}</h1>
            <p className="text-muted-foreground">{t("dashboard.admin.subtitle")}</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Logo on far right */}
            <img
              src="/logo.png"
              alt="Knowvest.plus"
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>

        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
          <div className="hidden sm:flex gap-2">
            <Button
                variant="outline"
                className="gap-2"
                onClick={() => setIsExportDialogOpen(true)}
              >
                <Download className="w-4 h-4" />
                {t("admin.exportData")}
              </Button>
              <Link to="/admin/create-account">
                <Button
                  variant="outline"
                  className="gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {t("dashboard.admin.createAccount")}
                </Button>
              </Link>
            <Link to="/admin/create-project-sale">
              <Button
                variant="outline"
                className="gap-2"
              >
                <ShoppingCart className="w-4 h-4" />
                {t("projectSale.createSaleTitle") || "Create Project Sale"}
              </Button>
            </Link>
            <Link to="/admin/data-migration">
              <Button
                variant="outline"
                className="gap-2"
              >
                <FileText className="w-4 h-4" />
                {t("navigation.DataMigration")}
              </Button>
            </Link>
            <Link to="/home">
              <Button variant="outline" className="gap-2">
                <Home className="w-4 h-4" />
                {t("navigation.home")}
              </Button>
            </Link>
            <Link to="/admin/settings">
              <Button variant="outline" className="gap-2">
                <Settings className="w-4 h-4" />
                {t("common.settings")}
              </Button>
            </Link>
            <Button
              variant="destructive"
              className="gap-2"
              onClick={() => {
                logout();
                navigate("/home");
              }}
            >
              <LogOut className="w-4 h-4" />
              {t("common.logout")}
            </Button>
          </div>
        </div>

        {/* Removed inline create account form. Now redirects to a separate page. */}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-muted/50 p-1.5 rounded-xl mb-6">
            <TabsTrigger value="overview" className="flex-1 min-w-[100px] gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg py-2.5 transition-all">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">{t("dashboard.admin.overview")}</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex-1 min-w-[100px] gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg py-2.5 transition-all">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">{t("dashboard.admin.userManagement")}</span>
            </TabsTrigger>
            <TabsTrigger value="projects" className="flex-1 min-w-[100px] gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg py-2.5 transition-all">
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">{t("dashboard.admin.projectManagement")}</span>
            </TabsTrigger>
            <TabsTrigger value="investments" className="flex-1 min-w-[100px] gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg py-2.5 transition-all">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">{t("dashboard.admin.investmentManagement")}</span>
            </TabsTrigger>
            <TabsTrigger value="sales" className="flex-1 min-w-[100px] gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg py-2.5 transition-all">
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden sm:inline">{t("dashboard.admin.sellingManagement")}</span>
            </TabsTrigger>
            <TabsTrigger value="banners" className="flex-1 min-w-[100px] gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md rounded-lg py-2.5 transition-all">
              <Megaphone className="w-4 h-4" />
              <span className="hidden sm:inline">{t("dashboard.admin.bannerManagement") || "Banners"}</span>
            </TabsTrigger>
          </TabsList>

          {/* ==================== OVERVIEW TAB ==================== */}
          <TabsContent value="overview">
            {/* Platform Statistics */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.totalRegisteredUsers")}</CardTitle>
                  <Users className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{((pendingUsers?.length || 0) + (approvedUsers?.length || 0) + (rejectedUsers?.length || 0) + (suspendedUsers?.length || 0))}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t("dashboard.admin.allTimeRegistrations")}</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.activeUsers")}</CardTitle>
                  <UserCheck className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{approvedUsers?.length || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t("dashboard.admin.approvedAndActive")}</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-amber-500">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.pendingApprovals")}</CardTitle>
                  <Clock className="h-4 w-4 text-amber-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{(pendingUsers?.length || 0) + (pendingProjects?.length || 0) + (pendingInvestments?.length || 0)}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t("dashboard.admin.usersProjectsInvestments")}</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.conversionRate")}</CardTitle>
                  <TrendingUp className="h-4 w-4 text-purple-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {((pendingUsers?.length || 0) + (approvedUsers?.length || 0) + (rejectedUsers?.length || 0) + (suspendedUsers?.length || 0)) > 0
                      ? Math.round(((approvedUsers?.length || 0) / ((pendingUsers?.length || 0) + (approvedUsers?.length || 0) + (rejectedUsers?.length || 0) + (suspendedUsers?.length || 0))) * 100)
                      : 0}%
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t("dashboard.admin.approvalRate")}</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* User Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {t("dashboard.admin.userBreakdown")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.activeInvestors")}</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {approvedUsers?.filter(u => u.role === "investor").length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.activeProjectHolders")}</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {approvedUsers?.filter(u => u.role === "project_holder").length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.admins")}</span>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {admins?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.suspendedUsers")}</span>
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      {suspendedUsers?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.pendingUsers")}</span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {pendingUsers?.length || 0}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Project Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    {t("dashboard.admin.projectStats")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.totalProjects")}</span>
                    <Badge variant="outline">{allProjects?.length || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.approvedProjects")}</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {approvedProjects?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.pendingProjects")}</span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {pendingProjects?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.activeProjects")}</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {allApprovedProjects?.length || 0}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Investment & Sales Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    {t("dashboard.admin.platformActivity")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.pendingInvestmentsCount")}</span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {pendingInvestments?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.confirmedInvestments")}</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {confirmedInvestments?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.pendingSalesCount")}</span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {pendingProjectSales?.length || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t("dashboard.admin.completedSales")}</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {completedProjectSales?.length || 0}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  {t("dashboard.admin.recentActivity")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingUsers && pendingUsers.slice(0, 3).map((user) => (
                    <div key={user._id} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium">{t("dashboard.admin.newUserRegistration")}</p>
                        <p className="text-xs text-muted-foreground">{user.name} {t("common.registeredAs")} {t(`auth.register.${user.role}`)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(user.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {approvedProjects && approvedProjects.slice(0, 3).map((project) => (
                    <div key={project._id} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium">{t("dashboard.admin.projectApproved")}</p>
                        <p className="text-xs text-muted-foreground">{project.title} {t("common.wasApproved")}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(project.updatedAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {pendingInvestments && pendingInvestments.slice(0, 3).map((investment) => (
                    <div key={investment._id} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium">{t("dashboard.admin.investmentRequested")}</p>
                        <p className="text-xs text-muted-foreground"><CurrencyDisplay amount={investment.amount} /> {t("common.investmentRequested")}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(investment.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {confirmedInvestments && confirmedInvestments.slice(0, 3).map((investment) => (
                    <div key={investment._id} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium">{t("dashboard.admin.investmentConfirmed")}</p>
                        <p className="text-xs text-muted-foreground"><CurrencyDisplay amount={investment.amount} /> {t("common.wasConfirmed")}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(investment.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {rejectedInvestments && rejectedInvestments.slice(0, 3).map((investment) => (
                    <div key={investment._id} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium">{t("dashboard.admin.investmentRejected")}</p>
                        <p className="text-xs text-muted-foreground"><CurrencyDisplay amount={investment.amount} /> {t("common.wasRejected")}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(investment.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {pendingUsers?.length === 0 && approvedProjects?.length === 0 && pendingInvestments?.length === 0 && confirmedInvestments?.length === 0 && rejectedInvestments?.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">{t("common.noRecentActivity")}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== USERS TAB ==================== */}
          <TabsContent value="users">
            <div className="grid sm:grid-cols-4 grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.pendingUsers")}</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{pendingUsers ? pendingUsers.length : 0}</div>
                  <p className="text-xs text-muted-foreground">{t("dashboard.admin.pendingUsers")}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.approvedUsers")}</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{approvedUsers ? approvedUsers.length : 0}</div>
                  <p className="text-xs text-muted-foreground">{t("dashboard.admin.approvedUsers")}</p>
                </CardContent>
              </Card>



              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.pendingProjects")}</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{pendingProjects ? pendingProjects.length : 0}</div>
                  <p className="text-xs text-muted-foreground">{t("dashboard.admin.pendingProjects")}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t("dashboard.admin.approvedProjects")}</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{approvedProjects ? approvedProjects.length : 0}</div>
                  <p className="text-xs text-muted-foreground">{t("dashboard.admin.approvedProjects")}</p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t("dashboard.admin.userManagement")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* User Role Tabs */}
                  <div className="flex border-b mb-4">
                    <button
                      className={`px-4 py-2 font-medium text-sm ${userRoleTab === "all" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setUserRoleTab("all")}
                    >
                      {t("dashboard.admin.allUsers")}
                    </button>
                    <button
                      className={`px-4 py-2 font-medium text-sm ${userRoleTab === "investor" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setUserRoleTab("investor")}
                    >
                      {t("auth.register.investor")}
                    </button>
                    <button
                      className={`px-4 py-2 font-medium text-sm ${userRoleTab === "project_holder" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setUserRoleTab("project_holder")}
                    >
                      {t("auth.register.projectHolder")}
                    </button>
                    <button
                      className={`px-4 py-2 font-medium text-sm ${userRoleTab === "admin" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setUserRoleTab("admin")}
                    >
                      {t("auth.register.admin")}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {userRoleTab === "all" && (
                      <>
                        {[...(pendingUsers || []), ...(approvedUsers || []), ...(rejectedUsers || []), ...(suspendedUsers || [])].map((user) => (
                          <div key={user._id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex items-center gap-4">
                              <Avatar>
                                <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                                <p className="text-xs text-muted-foreground">{t("common.role")}: {t(`auth.register.${user.role}`)} | {t("common.status")}: {t(`status.${user.status}`)}</p>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-2"
                                onClick={() => navigate(`/users/${user._id}`)}
                              >
                                <Eye className="w-4 h-4" />
                                {t("common.view")}
                              </Button>
                              {user.status === "pending_approval" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                                    onClick={() => handleApproveUser(user._id)}
                                  >
                                    <Check className="w-4 h-4" />
                                    {t("common.approve")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleRejectUser(user._id)}
                                  >
                                    <XCircle className="w-4 h-4" />
                                    {t("common.reject")}
                                  </Button>
                                </>
                              )}
                              {user.status !== "pending_approval" && user.status !== "suspended" && user.role !== "admin" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleSuspendUser(user._id)}
                                >
                                  <Ban className="w-4 h-4" />
                                  {t("common.suspend")}
                                </Button>
                              )}
                              {user.role !== "admin" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleDeleteUser(user._id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t("common.delete")}
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                        {pendingUsers?.length === 0 && approvedUsers?.length === 0 && rejectedUsers?.length === 0 && suspendedUsers?.length === 0 && (
                          <p className="text-muted-foreground text-center py-4">{t("common.noUsers")}</p>
                        )}
                      </>
                    )}

                    {userRoleTab === "investor" && (
                      <>
                        {investors && investors.length > 0 ? (
                          investors.map((user) => (
                            <div key={user._id} className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="flex items-center gap-4">
                                <Avatar>
                                  <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{user.name}</p>
                                  <p className="text-sm text-muted-foreground">{user.email}</p>
                                  <p className="text-xs text-muted-foreground">{t("common.status")}: {t(`status.${user.status}`)}</p>
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() => navigate(`/users/${user._id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                  {t("common.view")}
                                </Button>
                                {user.status === "pending_approval" && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                                      onClick={() => handleApproveUser(user._id)}
                                    >
                                      <Check className="w-4 h-4" />
                                      {t("common.approve")}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleRejectUser(user._id)}
                                    >
                                      <XCircle className="w-4 h-4" />
                                      {t("common.reject")}
                                    </Button>
                                  </>
                                )}
                                {user.status !== "pending_approval" && user.status !== "suspended" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleSuspendUser(user._id)}
                                  >
                                    <Ban className="w-4 h-4" />
                                    {t("common.suspend")}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleDeleteUser(user._id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t("common.delete")}
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-center py-4">{t("common.noInvestors")}</p>
                        )}
                      </>
                    )}

                    {userRoleTab === "project_holder" && (
                      <>
                        {projectHolders && projectHolders.length > 0 ? (
                          projectHolders.map((user) => (
                            <div key={user._id} className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="flex items-center gap-4">
                                <Avatar>
                                  <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{user.name}</p>
                                  <p className="text-sm text-muted-foreground">{user.email}</p>
                                  <p className="text-xs text-muted-foreground">{t("common.status")}: {t(`status.${user.status}`)}</p>
                                  {user.projectPurpose && (
                                    <p className="text-xs text-muted-foreground">{t("projectPurpose.title")}: {user.projectPurpose === "sell" ? t("projectPurpose.wantToSell") : t("projectPurpose.wantToGetInvestment")}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() => navigate(`/users/${user._id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                  {t("common.view")}
                                </Button>
                                {user.status === "pending_approval" && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                                      onClick={() => handleApproveUser(user._id)}
                                    >
                                      <Check className="w-4 h-4" />
                                      {t("common.approve")}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleRejectUser(user._id)}
                                    >
                                      <XCircle className="w-4 h-4" />
                                      {t("common.reject")}
                                    </Button>
                                  </>
                                )}
                                {user.status !== "pending_approval" && user.status !== "suspended" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleSuspendUser(user._id)}
                                  >
                                    <Ban className="w-4 h-4" />
                                    {t("common.suspend")}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleDeleteUser(user._id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t("common.delete")}
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-center py-4">{t("common.noProjectHolders")}</p>
                        )}
                      </>
                    )}

                    {userRoleTab === "admin" && (
                      <>
                        {admins && admins.length > 0 ? (
                          admins.map((user) => (
                            <div key={user._id} className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="flex items-center gap-4">
                                <Avatar>
                                  <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{user.name}</p>
                                  <p className="text-sm text-muted-foreground">{user.email}</p>
                                  <p className="text-xs text-muted-foreground">{t("common.status")}: {t(`status.${user.status}`)}</p>
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() => navigate(`/users/${user._id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                  {t("common.view")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleDeleteUser(user._id)}
                                  disabled // Admins cannot delete other admins for security
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t("common.delete")}
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-center py-4">{t("common.noAdmins")}</p>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== PROJECTS TAB ==================== */}
          <TabsContent value="projects">
            <div className="space-y-6">
              {/* Project Management */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("dashboard.admin.projectManagement")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Project Status Tabs */}
                  <div className="flex border-b mb-4">
                    <button
                      className={`px-4 py-2 font-medium text-sm ${projectStatusTab === "all" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setProjectStatusTab("all")}
                    >
                      {t("dashboard.admin.allProjects")}
                    </button>
                    <button
                      className={`px-4 py-2 font-medium text-sm ${projectStatusTab === "pending_approval" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setProjectStatusTab("pending_approval")}
                    >
                      {t("dashboard.admin.pendingProjects")}
                    </button>
                    <button
                      className={`px-4 py-2 font-medium text-sm ${projectStatusTab === "approved" ? "border-b-2 border-blue-500 text-blue-600" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setProjectStatusTab("approved")}
                    >
                      {t("dashboard.admin.approvedProjects")}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {projectStatusTab === "all" && (
                      <>
                        {allProjects && allProjects.length > 0 ? (
                          allProjects.map((project) => {
                            const projectOwner = getUserById(project.ownerId);
                            return (
                              <div key={project._id} className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <div className="flex flex-col sm:flex-row items-center gap-2 mb-1">
                                    <p className="font-medium">{project.title}</p>
                                    {project.projectPurpose === "sell" && (
                                      <Badge variant="destructive" className="text-xs">
                                        {t("projectPurpose.sellTag") || "For Sale"}
                                      </Badge>
                                    )}
                                    {project.projectPurpose === "getInvestment" && (
                                      <Badge variant="secondary" className="text-xs">
                                        {t("projectPurpose.investmentTag") || "For Investment"}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">{project.description}</p>
                                  <p className="text-xs text-muted-foreground">{t("common.by")}: {projectOwner?.name || project.ownerId}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2"
                                    onClick={() => {
                                      // Check project purpose to determine which detail page to navigate to
                                      if (project.projectPurpose === "sell") {
                                        navigate(`/project-sales/${project._id}`);
                                      } else {
                                        navigate(`/projects/${project._id}`);
                                      }
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                    {t("common.view")}
                                  </Button>
                                  {project.status === "pending_approval" && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                                        onClick={() => handleApproveProject(project._id)}
                                      >
                                        <Check className="w-4 h-4" />
                                        {t("common.approve")}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => handleRejectProject(project._id)}
                                      >
                                        <XCircle className="w-4 h-4" />
                                        {t("common.reject")}
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDeleteProject(project._id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {t("common.delete")}
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted-foreground text-center py-4">{t("common.noProjects")}</p>
                        )}
                      </>
                    )}

                    {projectStatusTab === "pending_approval" && (
                      <>
                        {pendingProjects && pendingProjects.length > 0 ? (
                          pendingProjects.map((project) => {
                            const projectOwner = getUserById(project.ownerId);
                            return (
                              <div key={project._id} className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <div className="flex flex-col sm:flex-row items-center gap-2 mb-1">
                                    <p className="font-medium">{project.title}</p>
                                    {project.projectPurpose === "sell" && (
                                      <Badge variant="destructive" className="text-xs">
                                        {t("projectPurpose.sellTag") || "For Sale"}
                                      </Badge>
                                    )}
                                    {project.projectPurpose === "getInvestment" && (
                                      <Badge variant="secondary" className="text-xs">
                                        {t("projectPurpose.investmentTag") || "For Investment"}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">{project.description}</p>
                                  <p className="text-xs text-muted-foreground">{t("common.by")}: {projectOwner?.name || project.ownerId}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2"
                                    onClick={() => {
                                      // Check project purpose to determine which detail page to navigate to
                                      if (project.projectPurpose === "sell") {
                                        navigate(`/project-sales/${project._id}`);
                                      } else {
                                        navigate(`/projects/${project._id}`);
                                      }
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                    {t("common.view")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                                    onClick={() => handleApproveProject(project._id)}
                                  >
                                    <Check className="w-4 h-4" />
                                    {t("common.approve")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleRejectProject(project._id)}
                                  >
                                    <XCircle className="w-4 h-4" />
                                    {t("common.reject")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDeleteProject(project._id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {t("common.delete")}
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted-foreground text-center py-4">{t("common.noPendingProjects")}</p>
                        )}
                      </>
                    )}

                    {projectStatusTab === "approved" && (
                      <>
                        {approvedProjects && approvedProjects.length > 0 ? (
                          approvedProjects.map((project) => {
                            const projectOwner = getUserById(project.ownerId);
                            return (
                              <div key={project._id} className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <div className="flex flex-col sm:flex-row items-center gap-2 mb-1">
                                    <p className="font-medium">{project.title}</p>
                                    {project.projectPurpose === "sell" && (
                                      <Badge variant="destructive" className="text-xs">
                                        {t("projectPurpose.sellTag") || "For Sale"}
                                      </Badge>
                                    )}
                                    {project.projectPurpose === "getInvestment" && (
                                      <Badge variant="secondary" className="text-xs">
                                        {t("projectPurpose.investmentTag") || "For Investment"}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">{project.description}</p>
                                  <p className="text-xs text-muted-foreground">{t("common.by")}: {projectOwner?.name || project.ownerId}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2"
                                    onClick={() => {
                                      // Check project purpose to determine which detail page to navigate to
                                      if (project.projectPurpose === "sell") {
                                        navigate(`/project-sales/${project._id}`);
                                      } else {
                                        navigate(`/projects/${project._id}`);
                                      }
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                    {t("common.view")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDeleteProject(project._id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {t("common.delete")}
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted-foreground text-center py-4">{t("common.noApprovedProjects")}</p>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== INVESTMENTS TAB ==================== */}
          <TabsContent value="investments">
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.admin.investmentManagement")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{pendingInvestments ? pendingInvestments.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.pendingInvestments")}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{projectHolderApprovedInvestments ? projectHolderApprovedInvestments.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.projectApprovedInvestments")}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{confirmedInvestments ? confirmedInvestments.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.confirmedInvestments")}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{rejectedInvestments ? rejectedInvestments.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.rejectedInvestments")}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {pendingInvestments && pendingInvestments.length > 0 && (
                    <>
                      <h3 className="font-medium">{t("dashboard.admin.pendingInvestments")}</h3>
                      {pendingInvestments.map((investment) => (
                        <div key={investment._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-muted/20 gap-4">
                          <div className="flex-1">
                            <p className="font-medium">{t("common.investmentOf")} <CurrencyDisplay amount={investment.amount} /></p>
                            <p className="text-sm text-muted-foreground">{t("common.by")}: {getUserById(investment.investorId)?.name || investment.investorId}</p>
                            <p className="text-xs text-muted-foreground">{t("common.project")}: {getProjectByIdHelper(investment.projectId)?.title || investment.projectId} | {t("common.created")}: {formatDate(investment.createdAt)}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                              onClick={() => handleApproveInvestment(investment._id)}
                            >
                              <Check className="w-4 h-4" />
                              {t("common.approve")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleRejectInvestment(investment._id)}
                            >
                              <XCircle className="w-4 h-4" />
                              {t("common.reject")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {projectHolderApprovedInvestments && projectHolderApprovedInvestments.length > 0 && (
                    <>
                      <h3 className="font-medium">{t("dashboard.admin.projectApprovedInvestments")}</h3>
                      {projectHolderApprovedInvestments.map((investment) => (
                        <div key={investment._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-muted/20 gap-4">
                          <div className="flex-1">
                            <p className="font-medium">{t("common.investmentOf")} <CurrencyDisplay amount={investment.amount} /></p>
                            <p className="text-sm text-muted-foreground">{t("common.by")}: {getUserById(investment.investorId)?.name || investment.investorId}</p>
                            <p className="text-xs text-muted-foreground">{t("common.project")}: {getProjectByIdHelper(investment.projectId)?.title || investment.projectId} | {t("common.created")}: {formatDate(investment.createdAt)}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                              onClick={() => handleApproveInvestment(investment._id)}
                            >
                              <Check className="w-4 h-4" />
                              {t("common.approve")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleRejectInvestment(investment._id)}
                            >
                              <XCircle className="w-4 h-4" />
                              {t("common.reject")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {confirmedInvestments && confirmedInvestments.length > 0 && (
                    <>
                      <h3 className="font-medium">{t("dashboard.admin.confirmedInvestments")}</h3>
                      {confirmedInvestments.map((investment) => (
                        <div key={investment._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-green-50 border-green-200 gap-4">
                          <div className="flex-1">
                            <p className="font-medium">{t("common.investmentOf")} <CurrencyDisplay amount={investment.amount} /></p>
                            <p className="text-sm text-muted-foreground">{t("common.by")}: {getUserById(investment.investorId)?.name || investment.investorId}</p>
                            <p className="text-xs text-muted-foreground">{t("common.project")}: {getProjectByIdHelper(investment.projectId)?.title || investment.projectId} | {t("common.created")}: {formatDate(investment.createdAt)}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleRefundInvestment(investment._id)}
                            >
                              <XCircle className="w-4 h-4" />
                              {t("common.refund")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {(!pendingInvestments || pendingInvestments.length === 0) && (!projectHolderApprovedInvestments || projectHolderApprovedInvestments.length === 0) && (!confirmedInvestments || confirmedInvestments.length === 0) && (
                    <p className="text-muted-foreground text-center py-4">{t("common.noInvestments")}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== SALES TAB ==================== */}
          <TabsContent value="sales">
            {/* Selling Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex flex-col sm:flex-row items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  {t("dashboard.admin.sellingManagement")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{pendingProjectSales ? pendingProjectSales.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.pendingSales")}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{acceptedProjectSales ? acceptedProjectSales.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.acceptedSales")}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{completedProjectSales ? completedProjectSales.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.completedSales")}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg border">
                    <div className="text-2xl font-bold">{cancelledProjectSales ? cancelledProjectSales.length : 0}</div>
                    <p className="text-sm text-muted-foreground">{t("dashboard.admin.cancelledSales")}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {pendingProjectSales && pendingProjectSales.length > 0 && (
                    <>
                      <h3 className="font-medium">{t("dashboard.admin.pendingSales")}</h3>
                      {pendingProjectSales.map((sale) => (
                        <div key={sale._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-muted/20 gap-4">
                          <div className="flex-1">
                            <p className="font-medium"><CurrencyDisplay amount={sale.saleAmount} /></p>
                            <p className="text-sm text-muted-foreground">{t("common.by")}: {getUserById(sale.sellerId)?.name || sale.sellerId}</p>
                            <p className="text-xs text-muted-foreground">{t("common.created")}: {formatDate(sale.createdAt)}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2"
                              onClick={() => navigate(`/project-sales/${sale.projectId}`)}
                            >
                              <Eye className="w-4 h-4" />
                              {t("common.view")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {acceptedProjectSales && acceptedProjectSales.length > 0 && (
                    <>
                      <h3 className="font-medium">{t("dashboard.admin.acceptedSales")}</h3>
                      {acceptedProjectSales.map((sale) => (
                        <div key={sale._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200 gap-4">
                          <div className="flex-1">
                            <p className="font-medium"><CurrencyDisplay amount={sale.saleAmount} /></p>
                            <p className="text-sm text-muted-foreground">{t("common.by")}: {getUserById(sale.sellerId)?.name || sale.sellerId} | {t("common.to")}: {getUserById(sale.buyerId!)?.name || sale.buyerId}</p>
                            <p className="text-xs text-muted-foreground">{t("common.created")}: {formatDate(sale.createdAt)}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2"
                              onClick={() => navigate(`/project-sales/${sale.projectId}`)}
                            >
                              <Eye className="w-4 h-4" />
                              {t("common.view")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {completedProjectSales && completedProjectSales.length > 0 && (
                    <>
                      <h3 className="font-medium">{t("dashboard.admin.completedSales")}</h3>
                      {completedProjectSales.map((sale) => (
                        <div key={sale._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-green-50 border-green-200 gap-4">
                          <div className="flex-1">
                            <p className="font-medium"><CurrencyDisplay amount={sale.saleAmount} /></p>
                            <p className="text-sm text-muted-foreground">{t("common.by")}: {getUserById(sale.sellerId)?.name || sale.sellerId} | {t("common.to")}: {getUserById(sale.buyerId!)?.name || sale.buyerId}</p>
                            <p className="text-xs text-muted-foreground">{t("common.created")}: {formatDate(sale.createdAt)}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2"
                              onClick={() => navigate(`/project-sales/${sale.projectId}`)}
                            >
                              <Eye className="w-4 h-4" />
                              {t("common.view")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {(!pendingProjectSales || pendingProjectSales.length === 0) && (!acceptedProjectSales || acceptedProjectSales.length === 0) && (!completedProjectSales || completedProjectSales.length === 0) && (
                    <p className="text-muted-foreground text-center py-4">{t("common.noSales")}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== BANNERS TAB ==================== */}
          <TabsContent value="banners">
            <BannerManagement />
          </TabsContent>
        </Tabs>
      </main>

      {/* Export Data Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.exportData")}</DialogTitle>
            <DialogDescription>
              {t("admin.exportDescription") || "Choose which categories to include in the Excel export."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="flex items-center justify-between border-b pb-2 mb-2">
              <span className="text-sm font-medium">{t("admin.categories")}</span>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => setExportSelections({ users: true, projects: true, investments: true, sales: true })}
                >
                  {t("admin.selectAll") || "Select All"}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => setExportSelections({ users: false, projects: false, investments: false, sales: false })}
                >
                  {t("admin.clearAll") || "Clear All"}
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="users" 
                checked={exportSelections.users} 
                onCheckedChange={(checked) => setExportSelections(prev => ({ ...prev, users: !!checked }))}
              />
              <label htmlFor="users" className="text-sm font-medium leading-none cursor-pointer">
                {t("admin.exportUsers")}
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="projects" 
                checked={exportSelections.projects} 
                onCheckedChange={(checked) => setExportSelections(prev => ({ ...prev, projects: !!checked }))}
              />
              <label htmlFor="projects" className="text-sm font-medium leading-none cursor-pointer">
                {t("admin.exportProjects")}
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="investments" 
                checked={exportSelections.investments} 
                onCheckedChange={(checked) => setExportSelections(prev => ({ ...prev, investments: !!checked }))}
              />
              <label htmlFor="investments" className="text-sm font-medium leading-none cursor-pointer">
                {t("admin.exportInvestments")}
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="sales" 
                checked={exportSelections.sales} 
                onCheckedChange={(checked) => setExportSelections(prev => ({ ...prev, sales: !!checked }))}
              />
              <label htmlFor="sales" className="text-sm font-medium leading-none cursor-pointer">
                {t("admin.exportSales")}
              </label>
            </div>
          </div>

          <DialogFooter className="flex sm:justify-between items-center">
            <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleExport} className="gap-2">
              <Download className="w-4 h-4" />
              {t("common.download")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;